import React, { useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Navbar from "../Navbar";
import Footer from "../Footer";
import RPC_METHODS from "./rpcMethods";
import defaultRpcUrl from "../../rpcurl";
import "../pecuscan-terminal.css";
import "./rpcexplorer.css";

function RpcExplorer() {
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [paramsText, setParamsText] = useState("");
  const [confirmMutating, setConfirmMutating] = useState(false);
  const [endpoint, setEndpoint] = useState(defaultRpcUrl);
  const [status, setStatus] = useState(null); // { kind, text, ms, id }
  const [response, setResponse] = useState(null);
  const [sending, setSending] = useState(false);
  const detailRef = useRef(null);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return RPC_METHODS;
    return RPC_METHODS.filter(
      (m) =>
        m.method.toLowerCase().includes(f) ||
        m.category.toLowerCase().includes(f)
    );
  }, [filter]);

  const grouped = useMemo(() => {
    const byCategory = new Map();
    for (const m of filtered) {
      if (!byCategory.has(m.category)) byCategory.set(m.category, []);
      byCategory.get(m.category).push(m);
    }
    return Array.from(byCategory.entries());
  }, [filtered]);

  const selectMethod = (m) => {
    setSelected(m);
    setParamsText(JSON.stringify(m.params, null, 2));
    setConfirmMutating(false);
    setStatus(null);
    setResponse(null);
    if (detailRef.current && window.innerWidth <= 900) {
      detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const runMethod = async () => {
    if (!selected) return;
    let params;
    try {
      params = JSON.parse(paramsText);
      if (!Array.isArray(params)) throw new Error("params must be a JSON array");
    } catch (e) {
      setStatus({ kind: "bad", text: "INVALID PARAMS JSON", detail: e.message });
      setResponse(null);
      return;
    }

    const payload = {
      jsonrpc: "2.0",
      id: Date.now(),
      method: selected.method,
      params,
    };

    setSending(true);
    setStatus({ kind: "muted", text: "SENDING…" });
    setResponse(null);

    const start = performance.now();
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const ms = Math.round(performance.now() - start);
      const data = await res.json();
      setStatus({
        kind: data.error ? "bad" : "ok",
        text: data.error ? "RPC ERROR" : `${res.status} OK`,
        ms,
        id: payload.id,
      });
      setResponse(JSON.stringify(data, null, 2));
    } catch (e) {
      setStatus({ kind: "bad", text: "REQUEST FAILED", detail: e.message });
      setResponse(null);
    } finally {
      setSending(false);
    }
  };

  const curl =
    selected &&
    `curl -X POST ${endpoint} \\\n  -H 'Content-Type: application/json' \\\n  -d '${JSON.stringify(
      { jsonrpc: "2.0", id: 1, method: selected.method, params: selected.params }
    )}'`;

  return (
    <Box>
      <Navbar />
      <section className="term-detail-page">
        <div className="container">
          <div className="term-detail-header">
            <div className="term-detail-crumb">
              <Link to="/">HOME</Link>
              <span>/</span>
              <span>DEVELOPERS</span>
              <span>/</span>
              <span className="term-detail-crumb-active">RPC ENDPOINTS</span>
            </div>
            <div className="term-detail-title-row">
              <h1 className="term-detail-title">▸ JSON-RPC EXPLORER</h1>
            </div>
            <div className="rpc-page-sub">
              POST / — every JSON-RPC 2.0 method the PecuNovus bridge answers.
              Pure blockchain state, zero price/$ data anywhere in this service.
            </div>
          </div>

          <div className="rpc-endpoint-bar">
            <span className="rpc-endpoint-lbl">ENDPOINT</span>
            <input
              type="text"
              className="rpc-endpoint-input"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              spellCheck="false"
            />
            <span className="rpc-endpoint-note">POST · application/json</span>
          </div>

          <div className="rpc-toolbar">
            <input
              type="text"
              className="rpc-filter"
              placeholder="Filter methods…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              spellCheck="false"
            />
            <span className="rpc-count">
              {filtered.length} / {RPC_METHODS.length} METHODS
            </span>
          </div>

          <div className="rpc-layout">
            <div className="rpc-list">
              {grouped.length === 0 && (
                <div className="rpc-list-empty">NO METHODS MATCH</div>
              )}
              {grouped.map(([cat, methods]) => (
                <div key={cat}>
                  <div className="rpc-cat-label">{cat}</div>
                  {methods.map((m) => (
                    <div
                      key={m.method}
                      className={
                        "rpc-method-item" +
                        (selected && selected.method === m.method
                          ? " selected"
                          : "")
                      }
                      onClick={() => selectMethod(m)}
                    >
                      <span className="rpc-m-name">{m.method}</span>
                      {m.mutating && <span className="rpc-badge bad">WRITE</span>}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="rpc-detail" ref={detailRef}>
              {!selected && (
                <div className="rpc-empty-state">
                  Pick a method on the left to see its description, editable
                  params, and run it live against the bridge.
                </div>
              )}

              {selected && (
                <>
                  <h3 className="rpc-detail-title">
                    {selected.method}{" "}
                    {selected.mutating ? (
                      <span className="rpc-badge bad">MUTATING</span>
                    ) : (
                      <span className="rpc-badge ok">READ-ONLY</span>
                    )}
                  </h3>
                  <div className="rpc-detail-meta">
                    {selected.category}
                    {selected.mirrorOf && (
                      <>
                        {" · "}
                        <span className="rpc-mirror-note">
                          alias of {selected.mirrorOf}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="rpc-m-desc">{selected.desc}</div>

                  {selected.mutating && (
                    <div className="rpc-warning">
                      This method changes real state — it will attempt to
                      broadcast an actual transaction against this bridge. Don't
                      run it unless you mean to.
                    </div>
                  )}

                  <div className="rpc-field-label">
                    Params (JSON array — edit freely)
                  </div>
                  <textarea
                    className="rpc-params-input"
                    value={paramsText}
                    onChange={(e) => setParamsText(e.target.value)}
                    spellCheck="false"
                  />

                  <div className="rpc-run-row">
                    <button
                      className="rpc-send-btn"
                      onClick={runMethod}
                      disabled={sending || (selected.mutating && !confirmMutating)}
                    >
                      {sending ? "SENDING…" : "SEND REQUEST"}
                    </button>
                    {selected.mutating && (
                      <label className="rpc-confirm">
                        <input
                          type="checkbox"
                          checked={confirmMutating}
                          onChange={(e) => setConfirmMutating(e.target.checked)}
                        />
                        I understand this may submit a real transaction
                      </label>
                    )}
                  </div>

                  {status && (
                    <div className="rpc-response-meta">
                      <span className={"rpc-badge " + status.kind}>
                        {status.text}
                      </span>
                      {status.ms !== undefined && (
                        <span className="rpc-stat">{status.ms} ms</span>
                      )}
                      {status.id !== undefined && (
                        <span className="rpc-stat">request id {status.id}</span>
                      )}
                      {status.detail && (
                        <span className="rpc-stat">{status.detail}</span>
                      )}
                    </div>
                  )}

                  {response && <pre className="rpc-response-body">{response}</pre>}

                  <div className="rpc-field-label rpc-curl-label">cURL</div>
                  <pre className="rpc-curl">{curl}</pre>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </Box>
  );
}

export default RpcExplorer;
